<template>
  <panel-navigation class="fill-height" :aiagent="true">
    <buy-number ref="buyNumber"></buy-number>

    <div class="retell-app-viewport bg-gray-50 flex flex-col h-screen overflow-hidden">
      <!-- Top Navigation Header -->
      <header v-if="me" class="retell-header-fixed bg-white border-b border-gray-200 flex-shrink-0">
        <!-- Desktop Header Layout (mdAndUp) -->
        <div class="d-none d-md-flex align-center justify-between fill-height px-3 w-full">
          <div class="d-flex align-center">
            <a href="javascript: void(0);" @click="$router.go(-1)" class="d-flex align-center mr-1 text-decoration-none">
              <i class="mdi mdi-chevron-left text-gray-600" style="font-size: 24px;"></i>
            </a>

            <!-- Editable Label -->
            <div class="d-flex align-center mr-3" v-if="activeSetting">
              <input
                type="text"
                v-model="activeSetting.label"
                class="retell-inline-input text-sm font-semibold text-gray-800"
                placeholder="Agent Label..."
                style="width: 300px;"
              />
            </div>

            <!-- Compact Agent Info & Controls Pill Bar -->
            <div v-if="me.integrations.bland.status == 1 && activeSetting" class="d-flex align-center gap-2">
              <!-- Phone Number Dropdown Selector + Copy Icon -->
              <div class="retell-pill">
                <i class="mdi mdi-phone-outline mr-1 text-gray-500 text-xs"></i>
                <select
                  v-model="blandNumberSelected"
                  @change="getIdFromBlandNumberSelect"
                  class="retell-select-clean text-xs font-medium mr-1"
                >
                  <option
                    v-for="(item, idx) in me.integrations.bland.settings"
                    :key="idx"
                    :value="item"
                  >
                    {{ item.phone_number }} {{ item.label ? `(${item.label})` : '' }}
                  </option>
                </select>
                <a
                  href="javascript: void(0);"
                  v-clipboard:copy="me.integrations.bland.settings[selected_bland_number].phone_number"
                  v-clipboard:success="onCopyAiNumber"
                  v-clipboard:error="onErrorAiNumber"
                  class="d-flex align-center text-gray-500 hover:text-gray-800 text-decoration-none"
                  title="Copy Phone Number"
                >
                  <i class="mdi mdi-content-copy" style="font-size: 14px;"></i>
                </a>
              </div>

              <!-- Model Selector Pill -->
              <div class="retell-pill">
                <i class="mdi mdi-cpu mr-1 text-gray-500 text-xs"></i>
                <select v-model="activeSetting.retell_model" class="retell-select-clean text-xs font-medium">
                  <option value="gpt-4.1">GPT 4.1</option>
                  <option value="gpt-4.1-mini">GPT 4.1 Mini</option>
                  <option value="gpt-4.1-nano">GPT 4.1 Nano</option>
                  <option value="gpt-5.1">GPT 5.1</option>
                  <option value="gpt-5">GPT 5</option>
                  <option value="gpt-5-mini">GPT 5 Mini</option>
                  <option value="gpt-5-nano">GPT 5 Nano</option>
                  <option value="claude-4.5-sonnet">Claude 4.5 Sonnet</option>
                  <option value="claude-4.5-haiku">Claude 4.5 Haiku</option>
                  <option value="gemini-2.5-flash">Gemini 2.5 Flash</option>
                  <option value="gemini-2.5-flash-lite">Gemini 2.5 Flash Lite</option>
                </select>
              </div>

              <!-- Voice Selection Pill -->
              <div class="retell-pill">
                <i class="mdi mdi-account-voice mr-1 text-gray-500 text-xs"></i>
                <select v-model="activeSetting.voice_retell" class="retell-select-clean text-xs font-medium">
                  <option
                    v-for="v in retellVoices"
                    :key="v.voice_id"
                    :value="v.voice_id"
                  >
                    {{ v.voice_name || v.voice_id }}
                  </option>
                </select>
              </div>
            </div>
          </div>

          <!-- High-Contrast Save Button -->
          <button
            class="retell-btn-primary text-xs font-semibold px-3 py-2 rounded-md"
            :class="{ 'opacity-50 pointer-events-none': savingAiSettings }"
            @click="saveBlandSettings()"
          >
            <i v-if="savingAiSettings" class="mdi mdi-loading mdi-spin mr-1"></i>
            Save Changes
          </button>
        </div>

        <!-- Mobile Header Layout (smAndDown) -->
        <div class="d-flex d-md-none flex-column w-full bg-white px-3 py-2">
          <!-- Mobile Top Row: Back, Label, and Save Changes -->
          <div class="d-flex align-center justify-between w-full">
            <div class="d-flex align-center flex-grow-1 mr-2" style="min-width: 0;">
              <a href="javascript: void(0);" @click="$router.go(-1)" class="d-flex align-center mr-1 text-decoration-none">
                <i class="mdi mdi-chevron-left text-gray-600" style="font-size: 24px;"></i>
              </a>

              <div v-if="activeSetting" class="d-flex align-center flex-grow-1" style="min-width: 0;">
                <input
                  type="text"
                  v-model="activeSetting.label"
                  class="retell-inline-input text-xs sm:text-sm font-bold text-gray-800 flex-grow-1"
                  placeholder="Agent Label..."
                  style="min-width: 60px; max-width: 140px;"
                />
                <i class="mdi mdi-pencil-outline text-gray-400 text-xs ml-0.5"></i>
              </div>
            </div>

            <div class="d-flex align-center gap-1.5 flex-shrink-0">
              <!-- Prominent Save Changes Button -->
              <button
                class="retell-btn-primary text-xs font-bold px-3 py-1.5 rounded-md d-flex align-center shadow-sm"
                :class="{ 'opacity-50 pointer-events-none': savingAiSettings }"
                @click="saveBlandSettings()"
              >
                <i v-if="savingAiSettings" class="mdi mdi-loading mdi-spin mr-1"></i>
                <i v-else class="mdi mdi-content-save mr-1"></i>
                <span>Save<span class="d-none d-sm-inline"> Changes</span></span>
              </button>
            </div>
          </div>

          <!-- Mobile Horizontally Scrollable Pills Strip -->
          <div v-if="me.integrations.bland.status == 1 && activeSetting" class="mobile-pills-bar d-flex align-center gap-2 mt-2 pb-0.5 overflow-x-auto w-full">
            <!-- Phone Number Selector -->
            <div class="retell-pill flex-shrink-0">
              <i class="mdi mdi-phone-outline mr-1 text-gray-500 text-xs"></i>
              <select
                v-model="blandNumberSelected"
                @change="getIdFromBlandNumberSelect"
                class="retell-select-clean text-xs font-medium mr-1"
                style="max-width: 140px;"
              >
                <option
                  v-for="(item, idx) in me.integrations.bland.settings"
                  :key="idx"
                  :value="item"
                >
                  {{ item.phone_number }} {{ item.label ? `(${item.label})` : '' }}
                </option>
              </select>
              <a
                href="javascript: void(0);"
                v-clipboard:copy="me.integrations.bland.settings[selected_bland_number] ? me.integrations.bland.settings[selected_bland_number].phone_number : activeSetting.phone_number"
                v-clipboard:success="onCopyAiNumber"
                v-clipboard:error="onErrorAiNumber"
                class="d-flex align-center text-gray-500 hover:text-gray-800 text-decoration-none"
                title="Copy Phone Number"
              >
                <i class="mdi mdi-content-copy" style="font-size: 13px;"></i>
              </a>
            </div>

            <!-- Model Selector Pill -->
            <div class="retell-pill flex-shrink-0">
              <i class="mdi mdi-cpu mr-1 text-gray-500 text-xs"></i>
              <select v-model="activeSetting.retell_model" class="retell-select-clean text-xs font-medium">
                <option value="gpt-4.1">GPT 4.1</option>
                <option value="gpt-4.1-mini">GPT 4.1 Mini</option>
                <option value="gpt-4.1-nano">GPT 4.1 Nano</option>
                <option value="gpt-5.1">GPT 5.1</option>
                <option value="gpt-5">GPT 5</option>
                <option value="gpt-5-mini">GPT 5 Mini</option>
                <option value="gpt-5-nano">GPT 5 Nano</option>
                <option value="claude-4.5-sonnet">Claude 4.5 Sonnet</option>
                <option value="claude-4.5-haiku">Claude 4.5 Haiku</option>
                <option value="gemini-2.5-flash">Gemini 2.5 Flash</option>
                <option value="gemini-2.5-flash-lite">Gemini 2.5 Flash Lite</option>
              </select>
            </div>

            <!-- Voice Selection Pill -->
            <div class="retell-pill flex-shrink-0">
              <i class="mdi mdi-account-voice mr-1 text-gray-500 text-xs"></i>
              <select v-model="activeSetting.voice_retell" class="retell-select-clean text-xs font-medium">
                <option
                  v-for="v in retellVoices"
                  :key="v.voice_id"
                  :value="v.voice_id"
                >
                  {{ v.voice_name || v.voice_id }}
                </option>
              </select>
            </div>
          </div>
        </div>
      </header>

      <!-- Main Workspace Container -->
      <div v-if="me" class="flex-grow-1 p-2 overflow-hidden d-flex retell-workspace-container">
        <!-- Disabled State -->
        <div v-if="me.integrations.bland.status == 0" class="w-full">
          <v-alert type="info" dense text class="text-xs">
            <strong>AI Not Enabled</strong>
          </v-alert>
          <div class="mt-2">
            <v-btn color="primary" small @click="$refs.buyNumber.show()">Assign AI Phone Number</v-btn>
          </div>
        </div>

        <template v-if="me.integrations.bland.status == 1 && activeSetting">
          <v-row dense class="fill-height flex-nowrap w-full ma-0 gap-2 retell-workspace-row">
            
            <!-- Left Column: Primary Prompts Canvas -->
            <v-col cols="12" md="7" class="fill-height d-flex flex-column pa-0 retell-canvas-col">
              
              <div class="retell-card mb-2 p-2">
                <div class="text-xs font-semibold text-gray-700 mb-2">Enabled agents</div>
                <div v-for="number in [activeSetting.phone_number].concat((activeSetting.additional_numbers || []).map(item => item.phone_number))" :key="number" class="text-xs mb-2">
                  <span class="mr-3">{{ number }}</span>
                  <label class="mr-3"><input type="checkbox" :checked="agentChannelEnabled(number, 'voice')" @change="setAgentChannel(number, 'voice', $event.target.checked)" :disabled="me && me.allow_changing_promp == 0 && !$store.state.auth.owner"> Voice agent</label>
                  <label><input type="checkbox" :checked="agentChannelEnabled(number, 'sms')" @change="setAgentChannel(number, 'sms', $event.target.checked)" :disabled="me && me.allow_changing_promp == 0 && !$store.state.auth.owner"> SMS agent</label>
                </div>
                <div class="text-xs text-gray-500">Save to apply. Editing prompts does not enable agents. With voice off, the number will not answer calls.</div>
              </div>
              <!-- System Prompt -->
              <div class="retell-card mb-2 p-2 flex-grow-1 d-flex flex-column">
                <div class="text-xs font-semibold text-gray-700 mb-1">Prompt</div>
                <textarea
                  v-model="activeSetting.call_prompt"
                  class="retell-textarea text-xs w-full flex-grow-1 px-2.5 py-2"
                  placeholder="Enter the primary instructions for your agent..."
                  :disabled="me && me.allow_changing_promp == 0 && !$store.state.auth.owner"
                ></textarea>
                <div class="text-caption text-gray-400 mt-1" style="font-size: 10px;">
                  The prompt is the top-level training of the agent. Changes will affect AI responses.
                  Scheduled follow-up wording is saved separately.
                  <button type="button" class="text-blue-600 underline" @click="settingsPanel = 3; $nextTick(() => $refs.followUpPanel.$el.scrollIntoView({ block: 'center' }))">Edit Follow-Up Settings</button>
                </div>
              </div>

              <!-- SMS Prompt -->
              <div class="retell-card mb-2 p-2 flex-grow-1 d-flex flex-column">
                <div class="text-xs font-semibold text-gray-700 mb-1">SMS Prompt</div>
                <textarea
                  v-model="activeSetting.sms_prompt"
                  class="retell-textarea text-xs w-full flex-grow-1 px-2.5 py-2"
                  placeholder="Enter SMS follow-up or automated response instructions..."
                  :disabled="me && me.allow_changing_promp == 0 && !$store.state.auth.owner"
                ></textarea>
                <div class="text-caption text-gray-400 mt-1" style="font-size: 10px;">
                  Update your SMS prompt.
                </div>
              </div>

              <!-- Opening Message -->
              <div class="retell-card p-2 flex-shrink-0">
                <div class="text-xs font-semibold text-gray-700 mb-1">Opening Message</div>
                <input
                  type="text"
                  v-model="activeSetting.first_sentence"
                  class="retell-input text-xs w-full px-2.5 py-2"
                  placeholder="e.g., Hello, thank you for calling..."
                />
                <div class="text-caption text-gray-400 mt-1" style="font-size: 10px;">
                  A phrase your call starts with instead of generating one on the fly.
                </div>
              </div>
            </v-col>

            <!-- Right Column: Single Unified Drawer Card -->
            <v-col cols="12" md="5" class="fill-height overflow-y-auto pa-0 pr-md-2 retell-drawer-col">
              <div class="retell-drawer-container">
                <v-expansion-panels v-model="settingsPanel" accordion flat tile class="retell-accordion">
                  
                  <!-- 1. Voice Settings -->
                  <v-expansion-panel class="retell-panel-border">
                    <v-expansion-panel-header class="text-xs font-semibold text-gray-700 py-2 px-2.5">
                      <span class="d-flex align-center"><i class="mdi mdi-volume-high mr-2 text-gray-500"></i> Voice Settings</span>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content class="px-2.5 pb-2">
                      <div class="d-flex align-center mb-1">
                        <div class="flex-grow-1 mr-2">
                          <label class="retell-label">Voice Selection</label>
                          <select v-model="activeSetting.voice_retell" class="retell-input text-xs px-2 py-1">
                            <option v-for="v in retellVoices" :key="v.voice_id" :value="v.voice_id">
                              {{ v.voice_name }} ({{ v.voice_id }})
                            </option>
                          </select>
                        </div>
                        <button class="retell-btn-secondary text-xs px-2 py-1 mt-3.5 d-flex align-center" @click="playVoiceRetell" :disabled="playingVoice">
                          <i class="mdi mdi-play text-xs mr-1"></i> Play
                        </button>
                      </div>

                      <div class="mb-1" v-show="activeSetting.voice_source === 'retell'">
                        <label class="retell-label">Voice Model</label>
                        <select v-model="activeSetting.retell_voice_model.model" class="retell-input text-xs px-2 py-1">
                          <option value="eleven_turbo_v2">eleven_turbo_v2</option>
                          <option value="eleven_flash_v2">eleven_flash_v2</option>
                          <option value="eleven_turbo_v2_5">eleven_turbo_v2_5</option>
                          <option value="eleven_flash_v2_5">eleven_flash_v2_5</option>
                          <option value="eleven_multilingual_v2">eleven_multilingual_v2</option>
                          <option value="Play3.0-mini">Play3.0-mini</option>
                          <option value="PlayDialog">PlayDialog</option>
                        </select>
                      </div>

                      <div class="mb-1">
                        <div class="d-flex justify-space-between align-center mb-0.5">
                          <label class="retell-label mb-0">Voice Speed</label>
                          <span class="text-xs text-gray-600 font-mono">{{ activeSetting.retell_voice_model.voice_speed }}</span>
                        </div>
                        <input type="range" min="0.5" max="2" step="0.1" v-model.number="activeSetting.retell_voice_model.voice_speed" class="retell-slider" />
                      </div>

                      <div class="mb-1">
                        <div class="d-flex justify-space-between align-center mb-0.5">
                          <label class="retell-label mb-0">Voice Temperature</label>
                          <span class="text-xs text-gray-600 font-mono">{{ activeSetting.retell_voice_model.voice_temperature }}</span>
                        </div>
                        <input type="range" min="0" max="2" step="0.1" v-model.number="activeSetting.retell_voice_model.voice_temperature" class="retell-slider" />
                      </div>

                      <div class="mb-1">
                        <div class="d-flex justify-space-between align-center mb-0.5">
                          <label class="retell-label mb-0">Voice Volume</label>
                          <span class="text-xs text-gray-600 font-mono">{{ activeSetting.retell_voice_model.volume }}</span>
                        </div>
                        <input type="range" min="0" max="2" step="0.1" v-model.number="activeSetting.retell_voice_model.volume" class="retell-slider" />
                      </div>
                    </v-expansion-panel-content>
                  </v-expansion-panel>

                  <!-- 2. KnowledgeBase Settings -->
                  <v-expansion-panel class="retell-panel-border">
                    <v-expansion-panel-header class="text-xs font-semibold text-gray-700 py-2 px-2.5">
                      <span class="d-flex align-center"><i class="mdi mdi-book-open-outline mr-2 text-gray-500"></i> KnowledgeBase</span>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content class="px-2.5 pb-2">
                      <p class="text-caption text-gray-500 mb-1" style="font-size: 10px;">
                        Upload documents or add website URLs to help the AI use your business information.
                      </p>

                      <v-file-input
                        accept="application/pdf,.docx,text/plain"
                        v-model="knowledgebase_file"
                        @change="onUploadKnowledgebase"
                        prepend-icon=""
                        prepend-inner-icon="mdi-file-upload"
                        label="Upload Knowledgebase"
                        outlined
                        dense
                        multiple
                        hide-details
                        class="retell-file-input mb-1"
                      />

                      <div v-if="uploading_knowledgebase" class="text-xs text-gray-500 my-1">
                        Uploading... please wait.
                      </div>

                      <div v-if="activeSetting.knowledgebase_files.length > 0" class="mb-1">
                        <strong class="text-xs text-gray-700">Uploaded Documents:</strong>
                        <div v-for="(item, index) in activeSetting.knowledgebase_files" :key="'knwfile-'+index" class="d-flex align-center justify-space-between py-0.5 text-xs border-b">
                          <span>{{ item.file_name }}</span>
                          <i class="mdi mdi-delete text-red-500 cursor-pointer" @click="deleteKnowledgebaseFile(index)"></i>
                        </div>
                      </div>

                      <div class="mt-2">
                        <label class="retell-label mb-1">Website URLs</label>
                        <div v-for="(item, kwwb_index) in activeSetting.knowledgebase_websites" :key="'knwbwebst-' + kwwb_index" class="d-flex align-center gap-2 mb-1">
                          <input
                            type="text"
                            v-model="activeSetting.knowledgebase_websites[kwwb_index]"
                            placeholder="https://example.com"
                            class="retell-input text-xs flex-grow-1 px-2 py-1"
                          />
                          <i class="mdi mdi-delete text-red-500 cursor-pointer" @click="deleteKnowledgebaseWebsite(kwwb_index)"></i>
                        </div>

                        <button class="retell-btn-secondary text-xs px-2 py-1 mt-1 font-medium" @click="activeSetting.knowledgebase_websites.push('')">
                          + Add Website
                        </button>
                        <p class="text-caption text-gray-400 mt-1" style="font-size: 10px;">
                          Use web content to prioritize your responses.
                        </p>
                      </div>
                    </v-expansion-panel-content>
                  </v-expansion-panel>

                  <!-- 3. Transfer Settings -->
                  <v-expansion-panel class="retell-panel-border">
                    <v-expansion-panel-header class="text-xs font-semibold text-gray-700 py-2 px-2.5">
                      <span class="d-flex align-center"><i class="mdi mdi-phone-forward mr-2 text-gray-500"></i> Transfer</span>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content class="px-2.5 pb-2">
                      <div class="mb-1">
                        <label class="retell-label">Description (Optional)</label>
                        <textarea
                          v-model="activeSetting.retell_transfer.description"
                          class="retell-textarea text-xs w-full px-2 py-2"
                          rows="2"
                        ></textarea>
                      </div>

                      <div class="mb-1">
                        <label class="retell-label">Transfer To</label>
                        <select v-model="activeSetting.retell_transfer.transfer_destination.type" class="retell-input text-xs px-2 py-1">
                          <option value="predefined">Static Phone Number</option>
                          <option value="inferred">Dynamic Routing Prompt</option>
                        </select>
                      </div>

                      <div v-if="activeSetting.retell_transfer.transfer_destination.type === 'predefined'" class="mb-1">
                        <label class="retell-label">Static Phone Number</label>
                        <input
                          type="text"
                          v-model="activeSetting.retell_transfer.transfer_destination.number"
                          class="retell-input text-xs w-full px-2 py-1"
                        />
                      </div>

                      <div v-if="activeSetting.retell_transfer.transfer_destination.type === 'inferred'" class="mb-1">
                        <label class="retell-label">Dynamic Routing Prompt</label>
                        <input
                          type="text"
                          v-model="activeSetting.retell_transfer.transfer_destination.prompt"
                          class="retell-input text-xs w-full px-2 py-1"
                        />
                      </div>

                      <div class="mb-1">
                        <label class="retell-label">Type</label>
                        <select v-model="activeSetting.retell_transfer.transfer_type" class="retell-input text-xs px-2 py-1">
                          <option value="cold">Cold Routing</option>
                          <option value="warm">Warm Transfer</option>
                        </select>
                      </div>

                      <div v-if="activeSetting.retell_transfer.transfer_type === 'cold'" class="mb-1">
                        <label class="retell-label">Displayed Phone Number</label>
                        <select v-model="activeSetting.retell_transfer.displayed_phone_number" class="retell-input text-xs px-2 py-1">
                          <option value="retell">Retell Agent's Number</option>
                          <option value="transferee">Transferee's Number</option>
                        </select>
                      </div>

                      <div v-if="activeSetting.retell_transfer.transfer_type === 'warm'" class="mb-2">
                        <label class="retell-label">Debrief Message Mode</label>
                        <select v-model="activeSetting.retell_transfer.warm_transfer_option.debrief_mode" class="retell-input text-xs px-2 py-1">
                          <option value="whisper">Whisper Debrief (Receiving Agent Only - Private)</option>
                          <option value="three_way">Three-Way Debrief (Both Caller & Agent - Public)</option>
                          <option value="both">Both (Whisper & Three-Way)</option>
                        </select>
                      </div>

                      <div v-if="activeSetting.retell_transfer.transfer_type === 'warm'" class="mb-1">
                        <label class="retell-label">Handoff Message Type</label>
                        <select v-model="activeSetting.retell_transfer.warm_transfer_option.type" class="retell-input text-xs px-2 py-1">
                          <option value="prompt">Prompt</option>
                          <option value="static_message">Static Sentence</option>
                        </select>

                        <div v-if="activeSetting.retell_transfer.warm_transfer_option.type === 'prompt'" class="mt-1">
                          <label class="retell-label">Warm Transfer Debrief Prompt</label>
                          <input
                            type="text"
                            v-model="activeSetting.retell_transfer.warm_transfer_option.prompt"
                            placeholder="Say hello to the agent and summarize the user problem to him."
                            class="retell-input text-xs w-full px-2 py-1"
                          />
                        </div>

                        <div v-if="activeSetting.retell_transfer.warm_transfer_option.type === 'static_message'" class="mt-1">
                          <label class="retell-label">Static Message</label>
                          <input
                            type="text"
                            v-model="activeSetting.retell_transfer.warm_transfer_option.message"
                            placeholder="You can take it from here."
                            class="retell-input text-xs w-full px-2 py-1"
                          />
                        </div>
                      </div>
                    </v-expansion-panel-content>
                  </v-expansion-panel>

                  <!-- 4. Follow-Up Settings -->
                  <v-expansion-panel ref="followUpPanel" class="retell-panel-border">
                    <v-expansion-panel-header class="text-xs font-semibold text-gray-700 py-2 px-2.5">
                      <span class="d-flex align-center"><i class="mdi mdi-clock-outline mr-2 text-gray-500"></i> Follow-Up Settings</span>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content class="px-2.5 pb-2">
                      <br />

                      <div class="d-flex align-center justify-space-between my-1">
                        <span class="text-xs font-medium text-gray-700">Auto Follow-Up</span>
                        <input type="checkbox" v-model="activeSetting.auto_follow_up" class="retell-toggle" />
                      </div>

                      <div class="mt-2">
                        <label class="retell-label">Follow-Up Message</label>
                        <input
                          type="text"
                          v-model="activeSetting.follow_up_message"
                          class="retell-input text-xs w-full px-2 py-1"
                        />
                      </div>

                      <div class="mt-2">
                        <label class="retell-label">Follow-Up Minutes</label>
                        <select v-model="activeSetting.follow_up_minutes" class="retell-input text-xs px-2 py-1">
                          <option :value="5">5 minutes</option>
                          <option :value="10">10 minutes</option>
                          <option :value="15">15 minutes</option>
                        </select>
                      </div>

                      <br />

                      <div class="d-flex align-center justify-space-between my-1">
                        <span class="text-xs font-medium text-gray-700">Secondary Follow-Up</span>
                        <input type="checkbox" v-model="activeSetting.auto_follow_up2" class="retell-toggle" />
                      </div>

                      <div class="mt-2">
                        <label class="retell-label">Follow-Up Message</label>
                        <input
                          type="text"
                          v-model="activeSetting.follow_up2_message"
                          class="retell-input text-xs w-full px-2 py-1"
                        />
                      </div>

                      <div class="mt-2">
                        <label class="retell-label">Follow-Up Minutes</label>
                        <select v-model="activeSetting.follow_up2_minutes" class="retell-input text-xs px-2 py-1">
                          <option :value="5">5 minutes</option>
                          <option :value="10">10 minutes</option>
                          <option :value="15">15 minutes</option>
                        </select>
                      </div>
                    </v-expansion-panel-content>
                  </v-expansion-panel>

                  <!-- 5. Calendar Settings -->
                  <v-expansion-panel class="retell-panel-border">
                    <v-expansion-panel-header class="text-xs font-semibold text-gray-700 py-2 px-2.5">
                      <span class="d-flex align-center"><i class="mdi mdi-calendar-month mr-2 text-gray-500"></i> Calendar Settings</span>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content class="px-2.5 pb-2">
                      <div class="mb-1">
                        <label class="retell-label">Calendar Link</label>
                        <input
                          type="text"
                          v-model="activeSetting.calendar_link"
                          class="retell-input text-xs w-full px-2 py-1"
                        />
                      </div>

                      <div class="d-flex align-center justify-space-between my-1">
                        <span class="text-xs font-medium text-gray-700">Enable Calendar Integration</span>
                        <input type="checkbox" v-model="activeSetting.retell_llm_settings.calendar_enabled" class="retell-toggle" />
                      </div>

                      <div v-if="activeSetting.retell_llm_settings.calendar_enabled" class="mt-2">
                        <label class="retell-label">Calendar Type</label>
                        <select v-model="activeSetting.calendar_type" class="retell-input text-xs px-2 py-1">
                          <option value="GHL">GHL</option>
                          <option value="Calendly">Calendly</option>
                        </select>
                      </div>

                      <div v-if="activeSetting.retell_llm_settings.calendar_enabled && activeSetting.calendar_type === 'GHL'" class="mt-2">
                        <label class="retell-label">GHL Calendar Selection</label>
                        <v-select
                          v-model="activeSetting.selected_ghl_calendar_id"
                          :items="serviceOptions"
                          item-text="name"
                          item-value="id"
                          outlined
                          dense
                          hide-details
                          class="retell-v-select"
                        >
                          <template v-slot:item="{ item, on, attrs }">
                            <v-list-item v-if="item.header">
                              <v-list-item-content>
                                <v-list-item-title class="subtitle-2 font-weight-bold">{{ item.header }}</v-list-item-title>
                              </v-list-item-content>
                            </v-list-item>
                            <v-divider v-else-if="item.divider" />
                            <v-list-item v-else v-bind="attrs" v-on="on">
                              <v-list-item-content>
                                <v-list-item-title>{{ item.name }}</v-list-item-title>
                              </v-list-item-content>
                            </v-list-item>
                          </template>
                        </v-select>
                      </div>
                    </v-expansion-panel-content>
                  </v-expansion-panel>

                  <!-- 6. LLM Settings -->
                  <v-expansion-panel class="retell-panel-border">
                    <v-expansion-panel-header class="text-xs font-semibold text-gray-700 py-2 px-2.5">
                      <span class="d-flex align-center"><i class="mdi mdi-brain mr-2 text-gray-500"></i> LLM Settings</span>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content class="px-2.5 pb-2">
                      <div class="mb-1">
                        <div class="d-flex justify-space-between align-center mb-0.5">
                          <label class="retell-label mb-0">LLM Temperature</label>
                          <span class="text-xs text-gray-600 font-mono">{{ activeSetting.retell_llm_settings.model_temperature }}</span>
                        </div>
                        <input type="range" min="0" max="1" step="0.05" v-model.number="activeSetting.retell_llm_settings.model_temperature" class="retell-slider" />
                      </div>

                      <div class="d-flex align-center justify-space-between my-1">
                        <span class="text-xs font-medium text-gray-700">Structured Output</span>
                        <input type="checkbox" v-model="activeSetting.retell_llm_settings.tool_call_strict_mode" class="retell-toggle" />
                      </div>

                      <div class="d-flex align-center justify-space-between my-1">
                        <span class="text-xs font-medium text-gray-700">High Priority</span>
                        <input type="checkbox" v-model="activeSetting.retell_llm_settings.model_high_priority" class="retell-toggle" />
                      </div>
                    </v-expansion-panel-content>
                  </v-expansion-panel>

                  <!-- 7. Speech Settings -->
                  <v-expansion-panel class="retell-panel-border">
                    <v-expansion-panel-header class="text-xs font-semibold text-gray-700 py-2 px-2.5">
                      <span class="d-flex align-center"><i class="mdi mdi-account-voice mr-2 text-gray-500"></i> Speech Settings</span>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content class="px-2.5 pb-2">
                      <div class="mb-1">
                        <label class="retell-label">Background Sound</label>
                        <select v-model="activeSetting.retell_speech_settings.ambient_sound" class="retell-input text-xs px-2 py-1">
                          <option :value="null">None</option>
                          <option value="coffee-shop">coffee-shop</option>
                          <option value="convention-hall">convention-hall</option>
                          <option value="summer-outdoor">summer-outdoor</option>
                          <option value="mountain-outdoor">mountain-outdoor</option>
                          <option value="static-noise">static-noise</option>
                          <option value="call-center">call-center</option>
                        </select>
                      </div>

                      <div class="mb-1">
                        <div class="d-flex justify-space-between align-center mb-0.5">
                          <label class="retell-label mb-0">Response Eagerness</label>
                          <span class="text-xs text-gray-600 font-mono">{{ activeSetting.retell_speech_settings.responsiveness }}</span>
                        </div>
                        <input type="range" min="0" max="1" step="0.01" v-model.number="activeSetting.retell_speech_settings.responsiveness" class="retell-slider" />
                        <label class="d-flex align-center mt-1 text-xs text-gray-600 cursor-pointer">
                          <input type="checkbox" v-model="activeSetting.retell_speech_settings.enable_dynamic_responsiveness" class="mr-2" />
                          Dynamically adjust based on user input
                        </label>
                      </div>

                      <div class="mb-1">
                        <div class="d-flex justify-space-between align-center mb-0.5">
                          <label class="retell-label mb-0">Interruption Sensitivity</label>
                          <span class="text-xs text-gray-600 font-mono">{{ activeSetting.retell_speech_settings.interruption_sensitivity }}</span>
                        </div>
                        <input type="range" min="0" max="1" step="0.01" v-model.number="activeSetting.retell_speech_settings.interruption_sensitivity" class="retell-slider" />
                        <div class="d-flex justify-space-between text-caption text-gray-400 mt-0.5" style="font-size: 10px;">
                          <span>blocks interruption</span>
                          <span>stops immediately</span>
                        </div>
                      </div>

                      <div class="mb-1">
                        <label class="retell-label">Reminder Message Frequency</label>
                        <div class="d-flex align-center gap-2">
                          <input type="number" v-model.number="activeSetting.retell_speech_settings.reminder_trigger_ms" class="retell-input text-xs w-16 text-center px-2 py-1" placeholder="10" />
                          <span class="text-xs text-gray-600">seconds</span>
                          <input type="number" v-model.number="activeSetting.retell_speech_settings.reminder_max_count" class="retell-input text-xs w-12 text-center px-2 py-1" placeholder="1" />
                          <span class="text-xs text-gray-600">times</span>
                        </div>
                      </div>

                      <div class="mb-1">
                        <label class="retell-label">Pronunciation Dictionary</label>
                        <div class="d-flex flex-wrap gap-1 mb-1">
                          <div v-for="(p, pIdx) in activeSetting.retell_speech_settings.pronunciation_dictionary" :key="pIdx" class="retell-chip text-xs">
                            <i class="mdi mdi-volume-source mr-1 text-gray-500"></i>
                            <span>{{ p.word }}</span>
                            <i class="mdi mdi-close ml-1 cursor-pointer" @click="removePronunciation(pIdx)"></i>
                          </div>
                        </div>
                        <div class="d-flex gap-1">
                          <input type="text" v-model="newPronunciationWord" placeholder="Word / Phrase" class="retell-input text-xs flex-grow-1 px-2 py-1" />
                          <button class="retell-btn-secondary text-xs px-2 py-1 font-medium" @click="addPronunciation">+ Add</button>
                        </div>
                      </div>

                      <div class="d-flex align-center justify-space-between my-1">
                        <span class="text-xs font-medium text-gray-700">Enable Backchannel</span>
                        <input type="checkbox" v-model="activeSetting.retell_speech_settings.enable_backchannel" class="retell-toggle" />
                      </div>

                      <div v-if="activeSetting.retell_speech_settings.enable_backchannel" class="pl-2 border-l-2 border-gray-200 my-1">
                        <div class="mb-1">
                          <label class="retell-label">Backchannel Frequency</label>
                          <input type="number" step="0.01" min="0" max="1" v-model.number="activeSetting.retell_speech_settings.backchannel_frequency" class="retell-input text-xs w-full px-2 py-1" />
                        </div>
                        <div class="mb-1">
                          <label class="retell-label">Backchannel Words</label>
                          <input type="text" v-model="activeSetting.retell_speech_settings.backchannel_words" class="retell-input text-xs w-full px-2 py-1" />
                        </div>
                      </div>

                      <div class="d-flex align-center justify-space-between my-1">
                        <span class="text-xs font-medium text-gray-700">Normalize for Speech</span>
                        <input type="checkbox" v-model="activeSetting.retell_speech_settings.normalize_for_speech" class="retell-toggle" />
                      </div>
                    </v-expansion-panel-content>
                  </v-expansion-panel>

                  <!-- 8. Call Settings -->
                  <v-expansion-panel class="retell-panel-border">
                    <v-expansion-panel-header class="text-xs font-semibold text-gray-700 py-2 px-2.5">
                      <span class="d-flex align-center"><i class="mdi mdi-phone-settings mr-2 text-gray-500"></i> Call Settings</span>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content class="px-2.5 pb-2">
                      <div class="d-flex align-center justify-space-between my-1">
                        <div>
                          <div class="text-xs font-medium text-gray-700">Voicemail Detection</div>
                          <div class="text-caption text-gray-400" style="font-size: 10px;">Hang up or leave message on voicemail detection</div>
                        </div>
                        <input type="checkbox" v-model="activeSetting.retell_call_settings.enable_voicemail_detection" class="retell-toggle" />
                      </div>

                      <div v-if="activeSetting.retell_call_settings.enable_voicemail_detection" class="pl-2 border-l-2 border-gray-200 my-1">
                        <div class="mb-1">
                          <label class="retell-label">Voicemail Message</label>
                          <input type="text" v-model="activeSetting.retell_call_settings.voicemail_message" class="retell-input text-xs w-full px-2 py-1" />
                        </div>
                        <div class="mb-1">
                          <label class="retell-label">Timeout (ms)</label>
                          <input type="number" v-model.number="activeSetting.retell_call_settings.voicemail_detection_timeout_ms" class="retell-input text-xs w-full px-2 py-1" />
                        </div>
                      </div>

                      <div class="d-flex align-center justify-space-between my-1">
                        <div>
                          <div class="text-xs font-medium text-gray-700">iOS / Android Call Screen Handling</div>
                          <div class="text-caption text-gray-400" style="font-size: 10px;">Enable predefined message for call screening</div>
                        </div>
                        <input type="checkbox" v-model="activeSetting.retell_call_settings.opt_out_sensitive_data_storage" class="retell-toggle" />
                      </div>

                      <div class="d-flex align-center justify-space-between my-1">
                        <div>
                          <div class="text-xs font-medium text-gray-700">User Keypad Input Detection</div>
                          <div class="text-caption text-gray-400" style="font-size: 10px;">Enable AI to listen for keypad input during call</div>
                        </div>
                        <input type="checkbox" v-model="activeSetting.retell_call_settings.allow_user_dtmf" class="retell-toggle" />
                      </div>

                      <div v-if="activeSetting.retell_call_settings.allow_user_dtmf" class="pl-2 border-l-2 border-gray-200 my-1">
                        <div class="mb-1">
                          <div class="d-flex justify-space-between align-center mb-0.5">
                            <label class="retell-label mb-0">Timeout</label>
                            <span class="text-xs text-gray-600 font-mono">{{ (activeSetting.retell_call_settings.user_dtmf_options.timeout_ms / 1000).toFixed(1) }} s</span>
                          </div>
                          <input type="range" min="500" max="10000" step="500" v-model.number="activeSetting.retell_call_settings.user_dtmf_options.timeout_ms" class="retell-slider" />
                        </div>

                        <div class="d-flex gap-2 mb-1">
                          <div class="flex-grow-1">
                            <label class="retell-label">Termination Key</label>
                            <input type="text" v-model="activeSetting.retell_call_settings.user_dtmf_options.termination_key" class="retell-input text-xs px-2 py-1" placeholder="#" />
                          </div>
                          <div class="flex-grow-1">
                            <label class="retell-label">Digit Limit</label>
                            <input type="number" v-model.number="activeSetting.retell_call_settings.user_dtmf_options.digit_limit" class="retell-input text-xs px-2 py-1" placeholder="10" />
                          </div>
                        </div>
                      </div>

                      <div class="mb-1 mt-2">
                        <div class="d-flex justify-space-between align-center mb-0.5">
                          <label class="retell-label mb-0">End Call on Silence</label>
                          <span class="text-xs text-gray-600 font-mono">{{ (activeSetting.retell_call_settings.end_call_after_silence_ms / 60000).toFixed(1) }} m</span>
                        </div>
                        <input type="range" min="10000" max="600000" step="10000" v-model.number="activeSetting.retell_call_settings.end_call_after_silence_ms" class="retell-slider" />
                      </div>

                      <div class="mb-1">
                        <div class="d-flex justify-space-between align-center mb-0.5">
                          <label class="retell-label mb-0">Max Call Duration</label>
                          <span class="text-xs text-gray-600 font-mono">{{ (activeSetting.retell_call_settings.max_call_duration_ms / 60000).toFixed(1) }} m</span>
                        </div>
                        <input type="range" min="60000" max="3600000" step="60000" v-model.number="activeSetting.retell_call_settings.max_call_duration_ms" class="retell-slider" />
                      </div>

                      <div class="mb-1">
                        <div class="d-flex justify-space-between align-center mb-0.5">
                          <label class="retell-label mb-0">Ring Duration</label>
                          <span class="text-xs text-gray-600 font-mono">{{ Math.round(activeSetting.retell_call_settings.ring_duration_ms / 1000) }} s</span>
                        </div>
                        <input type="range" min="5000" max="120000" step="5000" v-model.number="activeSetting.retell_call_settings.ring_duration_ms" class="retell-slider" />
                      </div>
                    </v-expansion-panel-content>
                  </v-expansion-panel>

                  <!-- 9. Chat Widget Settings -->
                  <v-expansion-panel class="retell-panel-border">
                    <v-expansion-panel-header class="text-xs font-semibold text-gray-700 py-2 px-2.5">
                      <span class="d-flex align-center"><i class="mdi mdi-message-text-outline mr-2 text-gray-500"></i> Chat Widget Settings</span>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content class="px-2.5 pb-2">
                      <div class="mb-1">
                        <label class="retell-label">Chat Box Title</label>
                        <input type="text" v-model="activeSetting.widget_title" class="retell-input text-xs w-full px-2 py-1" />
                      </div>

                      <div class="mb-1">
                        <label class="retell-label">Chat Box Description</label>
                        <input type="text" v-model="activeSetting.widget_description" class="retell-input text-xs w-full px-2 py-1" />
                      </div>

                      <div class="mb-1">
                        <label class="retell-label">Floating Chat Position</label>
                        <select v-model="activeSetting.floating_box_position" class="retell-input text-xs px-2 py-1">
                          <option value="Left">Left</option>
                          <option value="Centered">Centered</option>
                          <option value="Right">Right</option>
                        </select>
                      </div>

                      <div class="grid grid-cols-3 gap-2 my-2">
                        <div>
                          <label class="retell-label">Header</label>
                          <input type="color" v-model="activeSetting.widget_header_color" class="w-full h-6 cursor-pointer rounded" />
                        </div>
                        <div>
                          <label class="retell-label">Text</label>
                          <input type="color" v-model="activeSetting.widget_text_color" class="w-full h-6 cursor-pointer rounded" />
                        </div>
                        <div>
                          <label class="retell-label">Box</label>
                          <input type="color" v-model="activeSetting.widget_box_color" class="w-full h-6 cursor-pointer rounded" />
                        </div>
                        <div>
                          <label class="retell-label">Left Bubble</label>
                          <input type="color" v-model="activeSetting.widget_chat_bubble_left_color" class="w-full h-6 cursor-pointer rounded" />
                        </div>
                        <div>
                          <label class="retell-label">Right Bubble</label>
                          <input type="color" v-model="activeSetting.widget_chat_bubble_right_color" class="w-full h-6 cursor-pointer rounded" />
                        </div>
                        <div>
                          <label class="retell-label">Button</label>
                          <input type="color" v-model="activeSetting.widget_button_color" class="w-full h-6 cursor-pointer rounded" />
                        </div>
                      </div>

                      <div class="d-flex align-center justify-space-between my-1">
                        <span class="text-xs font-medium text-gray-700">Show Powered-By</span>
                        <input type="checkbox" v-model="activeSetting.show_powered_by" class="retell-toggle" />
                      </div>

                      <div class="mt-1">
                        <label class="retell-label">Powered By Link</label>
                        <input type="text" v-model="activeSetting.powered_by_link" class="retell-input text-xs w-full px-2 py-1" />
                      </div>

                      <div class="mt-1 mb-2">
                        <label class="retell-label">Powered By Text</label>
                        <input type="text" v-model="activeSetting.powered_by_text" class="retell-input text-xs w-full px-2 py-1" />
                      </div>

                      <div class="mt-2 border-t pt-2">
                        <label class="retell-label mb-1">Embed Code</label>
                        <v-tabs class="retell-tabs">
                          <v-tab class="text-xs">In Content</v-tab>
                          <v-tab class="text-xs">Floating</v-tab>

                          <v-tab-item class="pt-2">
                            <textarea :value="getEmbedCode()" class="retell-textarea text-xs w-full px-2 py-2 font-mono" rows="4" readonly></textarea>
                          </v-tab-item>

                          <v-tab-item class="pt-2">
                            <textarea :value="getEmbedCodeFloating()" class="retell-textarea text-xs w-full px-2 py-2 font-mono" rows="4" readonly></textarea>
                          </v-tab-item>
                        </v-tabs>
                      </div>
                    </v-expansion-panel-content>
                  </v-expansion-panel>

                </v-expansion-panels>

                <!-- Mobile Bottom Save Button (Visible only on mobile) -->
                <div class="d-flex d-md-none p-3 bg-white border-t border-gray-200">
                  <button
                    class="retell-btn-primary text-xs font-bold py-2.5 px-4 rounded-md w-full d-flex align-center justify-center shadow-sm"
                    :class="{ 'opacity-50 pointer-events-none': savingAiSettings }"
                    @click="saveBlandSettings()"
                  >
                    <i v-if="savingAiSettings" class="mdi mdi-loading mdi-spin mr-1"></i>
                    <i v-else class="mdi mdi-content-save mr-1"></i>
                    <span>Save Changes</span>
                  </button>
                </div>
              </div>
            </v-col>
          </v-row>
        </template>

        <!-- Error Alert -->
        <div v-if="errorBland" class="mt-2">
          <v-alert type="error" dense text class="text-xs">
            <div v-html="errorBland"></div>
          </v-alert>
        </div>
      </div>
    </div>
  </panel-navigation>
</template>

<script>
import axios from 'axios'
import { mapState } from 'vuex'
import PanelNavigation from '@/components/PanelNavigation.vue'
import BuyNumber from '@/components/dialogs/integrations/buyNumber.vue'
import moment from 'moment'

export default {
  metaInfo () {
    return { title: "AI Agent | " + this.$appName }
  },
  components: {
    PanelNavigation,
    BuyNumber
  },
  data() {
    return {
      selected_bland_number: 0,
      settingsPanel: undefined,
      blandNumberSelected: null,
      savingAiSettings: false,
      errorBland: null,
      playingVoice: false,
      knowledgebase_file: null,
      uploading_knowledgebase: false,
      retellVoices: [],
      teams: [],
      serviceOptions: [],
      selectedService: null,
      newPronunciationWord: ""
    }
  },
  computed: {
    ...mapState({
      me: state => state.me.me
    }),
    activeSetting() {
      if (this.me && this.me.integrations && this.me.integrations.bland && this.me.integrations.bland.settings) {
        const setting = this.me.integrations.bland.settings[this.selected_bland_number];
        if (setting) {
          if (!setting.retell_speech_settings) {
            this.$set(setting, 'retell_speech_settings', {
              ambient_sound: null,
              responsiveness: 0.95,
              enable_dynamic_responsiveness: false,
              interruption_sensitivity: 0.93,
              reminder_trigger_ms: 10000,
              reminder_max_count: 1,
              pronunciation_dictionary: [],
              enable_backchannel: false,
              backchannel_frequency: 0.8,
              backchannel_words: "",
              normalize_for_speech: true
            });
          }
          if (!setting.retell_call_settings) {
            this.$set(setting, 'retell_call_settings', {
              enable_voicemail_detection: false,
              voicemail_message: "",
              voicemail_detection_timeout_ms: 30000,
              opt_out_sensitive_data_storage: false,
              allow_user_dtmf: false,
              user_dtmf_options: { timeout_ms: 2500, termination_key: "#", digit_limit: 10 },
              end_call_after_silence_ms: 60000,
              max_call_duration_ms: 420000,
              ring_duration_ms: 30000
            });
          }
          if (!setting.retell_voice_model) {
            this.$set(setting, 'retell_voice_model', {
              model: 'eleven_turbo_v2',
              voice_speed: 1.0,
              voice_temperature: 1.0,
              volume: 1.0
            });
          }
          if (!setting.retell_transfer) {
            this.$set(setting, 'retell_transfer', {
              description: '',
              transfer_type: 'cold',
              displayed_phone_number: 'retell',
              transfer_destination: { type: 'predefined', number: '', prompt: '' },
              warm_transfer_option: { type: 'prompt', prompt: '', message: '', debrief_mode: 'whisper' }
            });
          } else if (setting.retell_transfer.warm_transfer_option && !setting.retell_transfer.warm_transfer_option.debrief_mode) {
            this.$set(setting.retell_transfer.warm_transfer_option, 'debrief_mode', 'whisper');
          }
          if (!setting.retell_llm_settings) {
            this.$set(setting, 'retell_llm_settings', {
              model_temperature: 0.7,
              tool_call_strict_mode: false,
              model_high_priority: false,
              calendar_enabled: false
            });
          }
          if (!setting.knowledgebase_files) this.$set(setting, 'knowledgebase_files', []);
          if (!setting.knowledgebase_websites) this.$set(setting, 'knowledgebase_websites', []);
        }
        return setting;
      }
      return null;
    }
  },
  methods: {
    onCopyAiNumber (e) {
      this.$swal({
        title: "Copied",
        text: "Phone number copied successfully!",
        showCancelButton: false,
      });
    },
    onErrorAiNumber (e) {
      alert('Failed to copy the phone number to the clipboard');
      console.log(e);
    },
    async fetchRetellVoices() {
      try {
        const response = await fetch('https://api.retellai.com/list-voices', {
          headers: {
            Authorization: 'Bearer key_76de52525950c499884ec1580872',
            'Content-Type': 'application/json'
          }
        });
        const data = await response.json();
        this.retellVoices = data || [];
      } catch (error) {
        console.error('Failed to fetch voices:', error);
      }
    },
    toArray (maybe) {
      if (Array.isArray(maybe)) return maybe
      if (!maybe) return []
      if (Array.isArray(maybe.data)) return maybe.data
      if (typeof maybe === 'string') {
        try { const p = JSON.parse(maybe); return Array.isArray(p) ? p : [] } catch (e) { return [] }
      }
      if (typeof maybe === 'object') return Object.values(maybe)
      return []
    },
    async getGhlCalendars () {
      try {
        const { data } = await axios.get(
          `${process.env.VUE_APP_API_ENDPOINT}/agency/get-ghl-calendars`,
          { headers: { Authorization: 'Bearer ' + this.$store.state.auth.jwt } }
        )
        const teams = Array.isArray(data) ? data : (Array.isArray(data.calendars) ? data.calendars : [])
        this.teams = teams
        const items = []
        teams.forEach(team => {
          items.push({ header: team.calendarConfig.description })
          this.toArray(team.services).forEach(svc => {
            items.push({ id: svc.id, name: svc.name, teamId: team.id, teamName: team.name })
          })
          items.push({ divider: true })
        })
        if (items.length && items[items.length - 1].divider) items.pop()
        this.serviceOptions = items
      } catch (e) {
        this.teams = []
        this.serviceOptions = []
      }
    },
    getIdFromBlandNumberSelect() {
      if (this.blandNumberSelected && this.me.integrations.bland.settings) {
        this.selected_bland_number = this.me.integrations.bland.settings.findIndex(
          x => x.phone_number === this.blandNumberSelected.phone_number
        );
      }
    },
    addPronunciation() {
      if (this.newPronunciationWord.trim() && this.activeSetting) {
        this.activeSetting.retell_speech_settings.pronunciation_dictionary.push({
          word: this.newPronunciationWord.trim(),
          alphabet: 'ipa',
          phoneme: ''
        });
        this.newPronunciationWord = '';
      }
    },
    removePronunciation(idx) {
      if (this.activeSetting) {
        this.activeSetting.retell_speech_settings.pronunciation_dictionary.splice(idx, 1);
      }
    },
    deleteKnowledgebaseFile(index) {
      if (this.activeSetting) {
        this.activeSetting.knowledgebase_files.splice(index, 1);
      }
    },
    deleteKnowledgebaseWebsite(index) {
      if (this.activeSetting) {
        this.activeSetting.knowledgebase_websites.splice(index, 1);
      }
    },
    async onUploadKnowledgebase() {
      if (this.knowledgebase_file !== null && this.activeSetting) {
        this.uploading_knowledgebase = true;
        let formData = new FormData();
        for (var i in this.knowledgebase_file) {
          formData.append("file[" + i + "]", this.knowledgebase_file[i]);
        }
        formData.append('phone_number', this.activeSetting.phone_number);

        try {
          let response = await axios.post(`${process.env.VUE_APP_API_ENDPOINT}/inbox/upload-knowledgebase`, formData, {
            headers: { 'Authorization': 'Bearer ' + this.$store.state.auth.jwt }
          });
          this.me.integrations.bland.settings = response.data.settings;
        } catch(e) {
          console.error(e);
        }
        this.knowledgebase_file = null;
        this.uploading_knowledgebase = false;
      }
    },
    playVoiceRetell() {
      let preview_audio_url = "";
      for (var i in this.retellVoices) {
        if (this.retellVoices[i].voice_id == this.activeSetting.voice_retell) {
          preview_audio_url = this.retellVoices[i].preview_audio_url;
        }
      }
      if (!preview_audio_url) return;

      this.playingVoice = true;
      const ctx = new AudioContext();
      fetch(preview_audio_url)
        .then(data => data.arrayBuffer())
        .then(arrayBuffer => ctx.decodeAudioData(arrayBuffer))
        .then(decodedAudio => {
          let playSound = ctx.createBufferSource();
          playSound.buffer = decodedAudio;
          playSound.connect(ctx.destination);
          playSound.start(ctx.currentTime);
          this.playingVoice = false;
        }).catch(() => { this.playingVoice = false; });
    },
    getEmbedCode() {
      return `<style>
iframe#acc-lsa-widget { width: 100%; height: 100%; min-height: 500px; border: solid 1px #CCCCCC !important; }
@media (max-width: 768px) { iframe#acc-lsa-widget { height: 60vh; min-height: 59vh; } }
</style> </head> <body>
<iframe id="acc-lsa-widget" src="${window.location.origin}/chat?u=${this.me.uuid}&p=${this.activeSetting ? this.activeSetting.phone_number : ''}"></iframe>`;
    },
    getEmbedCodeFloating() {
      return `<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@mdi/font@latest/css/materialdesignicons.min.css">
<style>
iframe#acc-lsa-widget { display: none; width: 450px; height: 550px; border: solid 1px #CCCCCC !important; position: fixed; right: 0px; bottom: 0px; margin-right: 15px; margin-bottom: 90px;z-index: 9999999999; }
@media (max-width: 768px) { iframe#acc-lsa-widget { width: 94%; height: 550px; } }
.ls-floating-button {border-radius: 10px;background: #3a9bdb;position: fixed;z-index: 9999999999;right: 0px;bottom: 0px;margin-right: 15px;margin-bottom: 15px;width: 60px;height: 46px;text-align: center;line-height: 50px;cursor: pointer;}
.ls-floating-button.close {display: none;color: #FFFFFF; font-size: 20px; height: 49px;}
</style>
<iframe id="acc-lsa-widget" src="${window.location.origin}/chat?u=${this.me.uuid}&p=${this.activeSetting ? this.activeSetting.phone_number : ''}&fl=1"></iframe>
<div class="ls-floating-button" onclick="document.getElementById('ls-floating-bubble').style.display = 'none'; document.querySelector('.ls-floating-button').style.display = 'none'; document.querySelector('.ls-floating-button.close').style.display = 'block'; document.querySelector('#acc-lsa-widget').style.display = 'block';"><i class="mdi mdi-message-text-outline" style="font-size: 30px; color: #FFFFFF;"></i></div>
<div class="ls-floating-button close" onclick="document.querySelector('.ls-floating-button').style.display = 'block'; document.querySelector('.ls-floating-button.close').style.display = 'none'; document.querySelector('#acc-lsa-widget').style.display = 'none';"><i class="mdi mdi-close" style="font-size: 35px; color: #FFFFFF;"></i></div>`;
    },
    agentChannelEnabled(number, channel) {
      const key = String(number || '').replace(/\D/g, '');
      const entry = (this.activeSetting.accelerate_agent_channels || {})[key];
      return !entry || entry[channel] === undefined || [true, 1, '1'].includes(entry[channel]);
    },
    setAgentChannel(number, channel, enabled) {
      const key = String(number || '').replace(/\D/g, '');
      if (!this.activeSetting.accelerate_agent_channels) this.$set(this.activeSetting, 'accelerate_agent_channels', {});
      if (!this.activeSetting.accelerate_agent_channels[key]) this.$set(this.activeSetting.accelerate_agent_channels, key, { voice: true, sms: true });
      this.$set(this.activeSetting.accelerate_agent_channels[key], channel, enabled);
    },
    async saveBlandSettings() {
      this.savingAiSettings = true;
      this.errorBland = null;

      let params = {
        api_key: this.me.integrations.bland.settings.api_key,
        phone_number_to_update: this.activeSetting.phone_number,
        accelerate_agent_channels: this.activeSetting.accelerate_agent_channels,
        label: this.activeSetting.label,
        call_prompt: this.activeSetting.call_prompt,
        separate_sms_prompt: this.activeSetting.separate_sms_prompt,
        sms_prompt: this.activeSetting.sms_prompt,
        gmail_prompt: this.activeSetting.gmail_prompt,
        voice_settings: this.activeSetting.voice_settings,
        temperature: this.activeSetting.temperature,
        voice: this.activeSetting.voice,
        voice_source: this.activeSetting.voice_source,
        voice_retell: this.activeSetting.voice_retell,
        retell_model: this.activeSetting.retell_model,
        model: this.activeSetting.model,
        business_phone_number: this.activeSetting.business_phone_number,
        calendar_link: this.activeSetting.calendar_link,
        calendar_enabled: this.activeSetting.calendar_enabled,
        calendar_type: this.activeSetting.calendar_type,
        selected_ghl_calendar_id: this.activeSetting.selected_ghl_calendar_id,
        book_what: this.activeSetting.book_what,
        auto_request_reviews: this.activeSetting.auto_request_reviews,
        auto_follow_up: this.activeSetting.auto_follow_up,
        follow_up_message: this.activeSetting.follow_up_message,
        follow_up_minutes: this.activeSetting.follow_up_minutes,
        auto_follow_up2: this.activeSetting.auto_follow_up2,
        follow_up2_message: this.activeSetting.follow_up2_message,
        follow_up2_minutes: this.activeSetting.follow_up2_minutes,
        widget_title: this.activeSetting.widget_title,
        widget_description: this.activeSetting.widget_description,
        floating_box_position: this.activeSetting.floating_box_position,
        widget_header_color: this.activeSetting.widget_header_color,
        widget_text_color: this.activeSetting.widget_text_color,
        widget_box_color: this.activeSetting.widget_box_color,
        widget_chat_bubble_left_color: this.activeSetting.widget_chat_bubble_left_color,
        widget_chat_bubble_right_color: this.activeSetting.widget_chat_bubble_right_color,
        widget_button_color: this.activeSetting.widget_button_color,
        knowledgebase_files: this.activeSetting.knowledgebase_files,
        knowledgebase_websites: this.activeSetting.knowledgebase_websites,
        recording_notification_message: this.activeSetting.recording_notification_message,
        pronunciation_guide: this.activeSetting.pronunciation_guide,
        first_sentence: this.activeSetting.first_sentence,
        retell_transfer: this.activeSetting.retell_transfer,
        retell_speech_settings: this.activeSetting.retell_speech_settings,
        retell_call_settings: this.activeSetting.retell_call_settings,
        retell_voice_model: this.activeSetting.retell_voice_model,
        retell_llm_settings: this.activeSetting.retell_llm_settings,
        show_powered_by: this.activeSetting.show_powered_by,
        powered_by_link: this.activeSetting.powered_by_link,
        powered_by_text: this.activeSetting.powered_by_text
      };

      try {
        let response = await axios.post(`${process.env.VUE_APP_API_ENDPOINT}/agency/save-bland-settings`, params, {
          headers: { 'Authorization': 'Bearer ' + this.$store.state.auth.jwt }
        });

        if (response.data.success) {
          this.errorBland = false;
          this.$swal({
            title: "Settings saved!",
            text: "Your AI settings have been successfully saved.",
            showCancelButton: false
          });
        } else {
          this.errorBland = response.data.error;
        }
      } catch(e) {
        console.error(e);
      }
      this.savingAiSettings = false;
    }
  },
  created() {
    this.fetchRetellVoices();
    this.getGhlCalendars();

    if (this.me && this.me.integrations && this.me.integrations.bland && this.me.integrations.bland.settings) {
      if (this.me.integrations.bland.status == 1) {
        this.blandNumberSelected = this.me.integrations.bland.settings[0];
      }
    }
  }
}
</script>

<style scoped>
/* Strict Top Header Fix on Desktop */
@media (min-width: 960px) {
  .retell-header-fixed {
    height: 48px !important;
    max-height: 48px !important;
    min-height: 48px !important;
  }
}

/* Retell Single-Card Drawer Container */
.retell-drawer-container {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  overflow: hidden;
}

/* Border Separators between Accordions */
.retell-panel-border {
  border-bottom: 1px solid #f1f5f9 !important;
}
.retell-panel-border:last-child {
  border-bottom: none !important;
}

/* High-Contrast Primary Save Button */
.retell-btn-primary {
  background-color: #0f172a !important;
  color: #ffffff !important;
  border: none;
  cursor: pointer;
  transition: background-color 0.15s ease;
}
.retell-btn-primary:hover {
  background-color: #1e293b !important;
}

.retell-textarea {
  resize: none !important;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  outline: none;
  color: #1e293b;
  width: 100%;
  padding: 10px;
}

.retell-input {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  outline: none;
  color: #1e293b;
  width: 100%;
  padding: 5px;
}

.retell-input:focus, .retell-textarea:focus {
  border-color: #94a3b8;
  background: #ffffff;
}

.retell-pill {
  display: flex;
  align-items: center;
  background-color: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 2px 8px;
  height: 28px;
  margin-right: 10px;
}

.retell-inline-input {
  border: 1px transparent;
  border-radius: 4px;
  padding: 2px 6px;
  background: transparent;
  outline: none;
}
.retell-inline-input:hover, .retell-inline-input:focus {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.retell-select-clean {
  background: transparent;
  border: none;
  outline: none;
  color: #334155;
  cursor: pointer;
}

.retell-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
}

.retell-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 2px;
}

.retell-slider {
  width: 100%;
  accent-color: #0f172a;
  height: 4px;
  border-radius: 2px;
  cursor: pointer;
}

.retell-toggle {
  accent-color: #10b981;
  width: 15px;
  height: 15px;
  cursor: pointer;
}

.retell-chip {
  display: inline-flex;
  align-items: center;
  background-color: #f1f5f9;
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  padding: 2px 8px;
  color: #334155;
}

.retell-btn-secondary {
  background-color: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  color: #334155;
  cursor: pointer;
  transition: background-color 0.15s ease;
}
.retell-btn-secondary:hover {
  background-color: #f8fafc;
}

.retell-accordion >>> .v-expansion-panel-header {
  min-height: 32px !important;
  padding: 6px 10px !important;
  background: #ffffff !important;
}

.retell-v-select >>> .v-input__control {
  min-height: 28px !important;
}

/* ========================================================
   Mobile-Responsive Enhancements (smAndDown / < 960px)
   ======================================================== */
@media (max-width: 959px) {
  /* Dynamic Sticky Mobile Header */
  .retell-header-fixed {
    height: auto !important;
    max-height: none !important;
    min-height: auto !important;
    position: sticky !important;
    top: 0 !important;
    z-index: 40 !important;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06) !important;
  }

  /* Full Page Natural Scrolling on Mobile */
  .retell-app-viewport {
    height: auto !important;
    min-height: 100vh !important;
    overflow-y: auto !important;
    -webkit-overflow-scrolling: touch !important;
  }

  /* Unconstrain Workspace Container */
  .retell-workspace-container {
    overflow-y: visible !important;
    overflow-x: hidden !important;
    height: auto !important;
    min-height: 0 !important;
    display: block !important;
    padding: 10px 8px 60px 8px !important;
  }

  /* Allow Columns to Stack Naturally */
  .retell-workspace-row {
    flex-wrap: wrap !important;
    height: auto !important;
    min-height: 0 !important;
    margin: 0 !important;
    gap: 0 !important;
  }

  /* Full-width Responsive Columns */
  .retell-canvas-col,
  .retell-drawer-col {
    height: auto !important;
    min-height: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
    flex: 0 0 100% !important;
    overflow-y: visible !important;
    padding: 0 !important;
  }

  .retell-canvas-col {
    margin-bottom: 16px !important;
  }

  /* Touch-Friendly Horizontal Pill Bar */
  .mobile-pills-bar {
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
  .mobile-pills-bar::-webkit-scrollbar {
    display: none;
  }

  .mobile-pills-bar .retell-pill {
    margin-right: 6px !important;
  }

  /* Expansion Panel Headers Touch Target Sizing & Clear Section Styling */
  .retell-accordion >>> .v-expansion-panel-header {
    min-height: 44px !important;
    padding: 11px 12px !important;
    font-size: 12.5px !important;
    font-weight: 600 !important;
    background-color: #f8fafc !important;
    border-bottom: 1px solid #f1f5f9 !important;
  }

  .retell-accordion >>> .v-expansion-panel-content__wrap {
    padding: 12px 10px !important;
  }

  /* System Prompt Card Comfort Height */
  .retell-textarea {
    min-height: 180px;
  }
}
</style>